import { useMemo, useState } from 'react'
import { Activity, Clipboard, Download, Search, Trash2, X } from 'lucide-react'
import { DIAGNOSTIC_RETENTION_DAYS, readDiagnosticEvents, setDiagnosticRetentionDays, type DiagnosticCategory, useDiagnosticsStore } from '../../diagnostics'
import { cleanFilename, formatDuration } from '../../utils'

type Filter = 'ALL' | 'ERRORS' | 'WARNINGS' | Exclude<DiagnosticCategory, 'SYSTEM' | 'ERROR'>
type ExportRange = 'session' | 'today' | 'yesterday' | 'date' | 'custom' | 'all'

const FILTERS: Filter[] = ['ALL', 'ERRORS', 'WARNINGS', 'PLAYER', 'MEDIA', 'NETWORK', 'BUFFER', 'VIDEO', 'AUDIO']

function bytes(value: number | null) {
  if (value === null) return '—'
  const units = ['B', 'KB', 'MB', 'GB']
  let amount = value; let index = 0
  while (amount >= 1024 && index < units.length - 1) { amount /= 1024; index += 1 }
  return `${amount.toFixed(index > 1 ? 1 : 0)} ${units[index]}`
}

function rate(value: number | null) {
  return value === null ? '—' : `${(value / 1_000_000).toFixed(2)} Mbps`
}

function clock(timestamp: string) {
  const date = new Date(timestamp)
  return `${date.toLocaleTimeString([], { hour12: false })}.${String(date.getMilliseconds()).padStart(3, '0')}`
}

function startOfDay(value: Date) { const date = new Date(value); date.setHours(0, 0, 0, 0); return date }
function endOfDay(value: Date) { const date = new Date(value); date.setHours(23, 59, 59, 999); return date }
function dateInput(value: Date) { return `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, '0')}-${String(value.getDate()).padStart(2, '0')}` }

export function DiagnosticsPanel() {
  const { events, visibleSince, summary, clearLiveView } = useDiagnosticsStore()
  const [filter, setFilter] = useState<Filter>('ALL')
  const [query, setQuery] = useState('')
  const [copied, setCopied] = useState(false)
  const [exportOpen, setExportOpen] = useState(false)
  const [exportRange, setExportRange] = useState<ExportRange>('session')
  const [format, setFormat] = useState<'json' | 'txt'>('json')
  const [specificDate, setSpecificDate] = useState(dateInput(new Date()))
  const [fromDate, setFromDate] = useState(dateInput(new Date()))
  const [toDate, setToDate] = useState(dateInput(new Date()))
  const [retentionDays, setRetentionDays] = useState(DIAGNOSTIC_RETENTION_DAYS)

  const liveEvents = useMemo(() => events.filter((event) => {
    if (event.timestamp < visibleSince) return false
    if (filter === 'ERRORS' && event.level !== 'ERROR') return false
    if (filter === 'WARNINGS' && event.level !== 'WARN') return false
    if (!['ALL', 'ERRORS', 'WARNINGS'].includes(filter) && event.category !== filter) return false
    const text = `${event.category} ${event.event} ${event.message} ${JSON.stringify(event.data ?? {})}`.toLowerCase()
    return !query || text.includes(query.toLowerCase())
  }), [events, filter, query, visibleSince])

  const droppedPercent = summary.performance.totalFrames
    ? (summary.performance.droppedFrames / summary.performance.totalFrames) * 100
    : 0
  const uptime = Math.max(0, (Date.now() - new Date(summary.session.startedAt).getTime()) / 1000)

  const sections = [
    { title: 'Player', rows: [
      ['Status', summary.player.status], ['Video', summary.player.title ? cleanFilename(summary.player.title) : '—'],
      ['Position', `${formatDuration(summary.player.position)} / ${formatDuration(summary.player.duration)}`], ['Rate', `${summary.player.playbackRate}×`],
      ['Volume', summary.player.muted ? `Muted (${Math.round(summary.player.volume * 100)}%)` : `${Math.round(summary.player.volume * 100)}%`],
      ['Fullscreen / PiP', `${summary.player.fullscreen ? 'Yes' : 'No'} / ${summary.player.pictureInPicture ? 'Yes' : 'No'}`],
    ]},
    { title: 'Media', rows: [
      ['Resolution', summary.media.resolution ?? '—'], ['Video / audio', `${summary.media.videoCodec ?? '—'} / ${summary.media.audioCodec ?? '—'}`],
      ['FPS / bitrate', `${summary.media.fps ?? '—'} / ${rate(summary.media.bitrate)}`], ['File size', bytes(summary.media.fileSize)],
      ['Source', summary.media.sourceType ?? '—'], ['Buffered', `${summary.media.bufferedSeconds.toFixed(1)} sec`],
    ]},
    { title: 'Performance', rows: [
      ['Frames', `${summary.performance.droppedFrames} dropped / ${summary.performance.totalFrames} total (${droppedPercent.toFixed(2)}%)`],
      ['Stalls', `${summary.performance.stalls} • ${(summary.performance.totalStallDurationMs / 1000).toFixed(1)} sec total`],
      ['Startup / first frame', summary.performance.startupTimeMs === null ? '—' : `${Math.round(summary.performance.startupTimeMs)} ms`],
      ['Last seek', summary.performance.lastSeekDurationMs === null ? '—' : `${Math.round(summary.performance.lastSeekDurationMs)} ms`],
      ['Buffer health', summary.performance.bufferHealth],
    ]},
    { title: 'Network', rows: [
      ['Status / estimate', `${summary.network.online ? 'Online' : 'Offline'} / ${summary.network.connectionSpeedMbps === null ? '—' : `${summary.network.connectionSpeedMbps} Mbps`}`],
      ['Media throughput', summary.network.mediaThroughputMbps === null ? '—' : `${summary.network.mediaThroughputMbps.toFixed(2)} Mbps`],
      ['Last HTTP / request', `${summary.network.httpStatus ?? '—'} / ${summary.network.requestDurationMs === null ? '—' : `${Math.round(summary.network.requestDurationMs)} ms`}`],
      ['Failures / retries / timeouts', `${summary.network.failedRequests} / ${summary.network.retries} / ${summary.network.timeouts}`],
    ]},
    { title: 'System', rows: [
      ['Browser', summary.system.browser], ['Operating system', summary.system.operatingSystem],
      ['Screen / viewport', `${summary.system.screenResolution} / ${summary.system.playerViewport}`], ['Pixel ratio', String(summary.system.devicePixelRatio)],
      ['Hardware decoding', summary.system.hardwareDecoding],
    ]},
    { title: 'Session', rows: [
      ['Session ID', summary.session.id], ['Player version', summary.session.appVersion],
      ['Started', new Date(summary.session.startedAt).toLocaleString()], ['Uptime', formatDuration(uptime)],
    ]},
  ]

  const copyDiagnostics = async () => {
    const important = events.filter((event) => event.level !== 'INFO')
    const report = { generatedAt: new Date().toISOString(), summary, warningsAndErrors: important, recentEvents: events.slice(-100) }
    await navigator.clipboard.writeText(JSON.stringify(report, null, 2))
    setCopied(true); window.setTimeout(() => setCopied(false), 1500)
  }

  const exportLog = async () => {
    const today = new Date(); let from: Date | undefined; let to: Date | undefined
    if (exportRange === 'session') from = new Date(summary.session.startedAt)
    if (exportRange === 'today') { from = startOfDay(today); to = endOfDay(today) }
    if (exportRange === 'yesterday') { const day = new Date(today); day.setDate(day.getDate() - 1); from = startOfDay(day); to = endOfDay(day) }
    if (exportRange === 'date') { from = startOfDay(new Date(`${specificDate}T00:00:00`)); to = endOfDay(from) }
    if (exportRange === 'custom') { from = startOfDay(new Date(`${fromDate}T00:00:00`)); to = endOfDay(new Date(`${toDate}T00:00:00`)) }
    const stored = await readDiagnosticEvents(from, to)
    const inRange = events.filter((event) => (!from || event.timestamp >= from.toISOString()) && (!to || event.timestamp <= to.toISOString()))
    const selected = [...new Map([...stored, ...inRange].map((event) => [event.id, event])).values()]
      .sort((a, b) => a.timestamp.localeCompare(b.timestamp))
    const content = format === 'json'
      ? JSON.stringify({ exportedAt: new Date().toISOString(), range: { from: from?.toISOString() ?? null, to: to?.toISOString() ?? null }, summary, events: selected }, null, 2)
      : selected.map((event) => `${clock(event.timestamp)}  ${event.level.padEnd(5)}  ${event.category.padEnd(7)}  ${event.message}${event.data ? ` • ${JSON.stringify(event.data)}` : ''}`).join('\n')
    const suffix = from && to ? `${dateInput(from)}-to-${dateInput(to)}` : from ? dateInput(from) : 'all'
    const anchor = document.createElement('a')
    anchor.href = URL.createObjectURL(new Blob([content], { type: format === 'json' ? 'application/json' : 'text/plain' }))
    anchor.download = `cloud-player-log-${suffix}.${format}`
    anchor.click(); URL.revokeObjectURL(anchor.href); setExportOpen(false)
  }

  return (
    <section>
      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-xs font-semibold uppercase tracking-wider text-dim">Diagnostics / Live Log</h3>
        <div className="flex items-center gap-2 text-xs text-muted"><Activity size={13} /> Retained {DIAGNOSTIC_RETENTION_DAYS} days</div>
      </div>

      <div className="space-y-3 rounded-lg border border-border bg-surface p-3">
        <div className="grid gap-2 sm:grid-cols-2">
          {sections.map((section) => (
            <div key={section.title} className="rounded border border-border/70 bg-panel/45 p-2.5">
              <p className="mb-2 text-[10px] font-bold uppercase tracking-[0.16em] text-accent">{section.title}</p>
              <dl className="space-y-1">
                {section.rows.map(([label, value]) => <div key={label} className="flex gap-2 text-[11px]"><dt className="shrink-0 text-muted">{label}</dt><dd className="ml-auto min-w-0 truncate text-right text-text" title={value}>{value}</dd></div>)}
              </dl>
            </div>
          ))}
        </div>

        <div className="flex flex-wrap items-center gap-2 border-t border-border/70 pt-3">
          <button onClick={() => void copyDiagnostics()} className="btn-ghost rounded px-2 py-1.5 text-xs"><Clipboard size={13} /> {copied ? 'Copied' : 'Copy Diagnostics'}</button>
          <button onClick={() => setExportOpen(true)} className="btn-ghost rounded px-2 py-1.5 text-xs"><Download size={13} /> Download Log</button>
          <button onClick={clearLiveView} className="btn-ghost rounded px-2 py-1.5 text-xs"><Trash2 size={13} /> Clear Live View</button>
          <label className="ml-auto flex items-center gap-2 text-[10px] text-muted">Retention
            <select className="rounded border border-border bg-panel px-1.5 py-1 text-xs text-text" value={retentionDays} onChange={(event) => { const days = Number(event.target.value); setRetentionDays(days); setDiagnosticRetentionDays(days) }}>
              <option value={7}>7 days</option><option value={14}>14 days</option><option value={30}>30 days</option><option value={90}>90 days</option>
            </select>
          </label>
        </div>

        <div className="space-y-2">
          <div className="flex flex-wrap gap-1">
            {FILTERS.map((item) => <button key={item} onClick={() => setFilter(item)} className={`rounded px-2 py-1 text-[10px] ${filter === item ? 'bg-accent text-white' : 'bg-panel text-muted hover:text-text'}`}>{item[0] + item.slice(1).toLowerCase()}</button>)}
          </div>
          <label className="relative block"><Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted" /><input value={query} onChange={(event) => setQuery(event.target.value)} className="input py-1.5 pl-8 text-xs" placeholder="Search event, message, or details" /></label>
          <div className="max-h-72 overflow-y-auto rounded border border-border/70 bg-void/70 font-mono">
            {liveEvents.length === 0 ? <p className="p-3 text-xs text-muted">No matching events in the live view.</p> : liveEvents.map((event) => (
              <div key={event.id} className="grid grid-cols-[88px_38px_52px_1fr] gap-2 border-b border-border/30 px-2 py-1.5 text-[10px] last:border-0">
                <span className="text-dim">{clock(event.timestamp)}</span><span className={event.level === 'ERROR' ? 'text-danger' : event.level === 'WARN' ? 'text-warning' : 'text-muted'}>{event.level}</span><span className="text-accent">{event.category}</span><span className="break-words text-text">{event.message}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {exportOpen && <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/75 p-4" onMouseDown={(event) => { if (event.target === event.currentTarget) setExportOpen(false) }}>
        <div className="panel w-full max-w-sm p-4 shadow-2xl">
          <div className="mb-4 flex items-center justify-between"><h4 className="font-semibold text-bright">Export diagnostics</h4><button onClick={() => setExportOpen(false)} className="btn-ghost rounded p-1.5"><X size={15} /></button></div>
          <label className="block text-xs text-muted">Range<select className="input mt-1" value={exportRange} onChange={(event) => setExportRange(event.target.value as ExportRange)}><option value="session">Current session</option><option value="today">Today</option><option value="yesterday">Yesterday</option><option value="date">Specific date</option><option value="custom">Custom date range</option><option value="all">All available logs</option></select></label>
          {exportRange === 'date' && <label className="mt-3 block text-xs text-muted">Date<input className="input mt-1" type="date" value={specificDate} onChange={(event) => setSpecificDate(event.target.value)} /></label>}
          {exportRange === 'custom' && <div className="mt-3 grid grid-cols-2 gap-2"><label className="text-xs text-muted">From<input className="input mt-1" type="date" value={fromDate} onChange={(event) => setFromDate(event.target.value)} /></label><label className="text-xs text-muted">To<input className="input mt-1" type="date" value={toDate} min={fromDate} onChange={(event) => setToDate(event.target.value)} /></label></div>}
          <div className="mt-4"><p className="mb-2 text-xs text-muted">Format</p><div className="grid grid-cols-2 gap-2"><button onClick={() => setFormat('json')} className={`rounded border p-2 text-xs ${format === 'json' ? 'border-accent bg-accent/10 text-accent' : 'border-border text-muted'}`}>JSON · detailed</button><button onClick={() => setFormat('txt')} className={`rounded border p-2 text-xs ${format === 'txt' ? 'border-accent bg-accent/10 text-accent' : 'border-border text-muted'}`}>TXT · readable</button></div></div>
          <button onClick={() => void exportLog()} className="btn-primary mt-4 w-full justify-center"><Download size={14} /> Export complete log</button>
        </div>
      </div>}
    </section>
  )
}
